/**
 * PromotionsCarousel Component
 * Phase D2.3: Cinematic Promotions Carousel
 *
 * Features:
 * - Large imagery matching TV player dimensions
 * - Text overlaid on images with gradient backgrounds
 * - Auto-rotation every 6 seconds
 * - Smooth fade transitions
 */

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Sparkles } from 'lucide-react';
import { promotions } from '@/data/promotionsData';

export function PromotionsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused]);

  // Go to specific slide
  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsPaused(true);
    // Resume auto-rotation after 10 seconds
    setTimeout(() => setIsPaused(false), 10000);
  }, []);

  return (
    <div className="promotions-carousel p-4 border-t border-white/[0.04]">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-zaxia-teal" />
        <span className="text-[10px] text-white/30 uppercase tracking-[0.1em] font-medium">
          Today's Offers
        </span>
      </div>

      {/* Carousel - matches TV player aspect ratio */}
      <div
        className="relative aspect-video rounded-xl overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Crossfade: show all images, animate opacity */}
        {promotions.map((promo, index) => (
          <motion.div
            key={promo.id}
            initial={false}
            animate={{
              opacity: index === currentIndex ? 1 : 0,
              scale: index === currentIndex ? 1 : 1.05
            }}
            transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
            style={{ zIndex: index === currentIndex ? 1 : 0 }}
          >
            {/* Background image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${promo.imageUrl})` }}
            />

            {/* Gradient overlay */}
            <div className={clsx(
              "absolute inset-0 bg-gradient-to-t",
              promo.gradient
            )} />

            {/* Content overlaid on image */}
            <div className="promotions-slide-content absolute inset-0 flex flex-col justify-end p-4">
              <motion.div
                animate={{
                  opacity: index === currentIndex ? 1 : 0,
                  y: index === currentIndex ? 0 : 10
                }}
                transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: index === currentIndex ? 0.3 : 0 }}
              >
                <h3 className="text-base font-semibold text-white mb-0.5 drop-shadow-lg">
                  {promo.title}
                </h3>
                <p className="text-sm text-zaxia-teal font-medium drop-shadow-md">
                  {promo.tagline}
                </p>
              </motion.div>
            </div>
          </motion.div>
        ))}

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/30">
          <motion.div
            key={`progress-${currentIndex}-${isPaused}`}
            className="h-full bg-zaxia-teal"
            initial={{ width: '0%' }}
            animate={{ width: isPaused ? '0%' : '100%' }}
            transition={{
              duration: isPaused ? 0 : 6,
              ease: 'linear'
            }}
          />
        </div>
      </div>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-3">
        {promotions.map((promo, i) => (
          <button
            key={promo.id}
            onClick={() => goToSlide(i)}
            className={clsx(
              'h-1.5 rounded-full transition-all duration-300',
              i === currentIndex
                ? 'bg-zaxia-teal w-5'
                : 'bg-white/20 hover:bg-white/40 w-1.5'
            )}
          />
        ))}
      </div>
    </div>
  );
}
